import type { SampleStage, SampleStatus } from '@ctcells/contract'
import { enumLabel, t } from '@ctcells/i18n'

import type { ChamberFigureState } from '../../figures/ChamberFigure.tsx'
import type { ChamberPhase } from '../../figures/chamber3d/chamberScene.ts'

import type { BundleState } from './patientChambers.ts'
import type { MonitoringDeviceKey } from './monitoringModel.ts'
import type { FlowCell, FlowCellState, StageVerdict } from './sampleFlowModel.ts'

/**
 * 흐름 어휘 — **카드의 단계 줄과 챔버 칩이 같은 말을 하게 하는 한 자리.**
 *
 * 🔴 두 자리가 **같은 출처·같은 어휘·같은 글리프**여야 한다. 그래서 글리프 표와 문구 규칙을
 *    **여기 하나**에 두고 나눠 쓴다. 한쪽에만 말을 더하면 한 카드가 두 말을 한다.
 * ⚠ 종전에는 이 표를 **격자**(`SampleFlow.tsx` · 2026-09-21 걷음)와 그림이 나눠 썼다.
 *   격자가 사라져도 표는 남는다 — 카드 «안»에서 같은 분업이 이어진다.
 *
 * 🔴 **색을 쓰지 않는다**(무채색 디자인 시스템). 상태는 **글리프 · 테두리 · 글자** 세 채널로
 *    갈린다 — 이 파일이 그중 둘(글리프 · 글자)을 쥔다.
 */

/** 🔴 **`StatusBadge` 와 같은 어휘다.** 이 화면만의 기호를 새로 만들지 않는다 */
const GLYPH: Record<FlowCellState | 'fail' | 'unassigned', string> = {
  marked: '●',
  active: '◐',
  pending: '○',
  halted: '—',
  fail: '▲',
  /** 🔴 「누구 것인지 모른다」 — `pending`(대기)과 **다른 기호**여야 한다 */
  unassigned: '·',
}

export function flowGlyph(state: FlowCellState | 'unassigned', verdict?: StageVerdict): string {
  return verdict === 'fail' ? GLYPH.fail : GLYPH[state]
}

/**
 * **글자가 상태의 정본이다.** 글리프는 거들 뿐이라 스크린리더가 읽는 것은 이 문구다.
 *
 * 🔴 **「성공」은 판정이 실제로 왔을 때만 나온다** — 안 오면 「지남」이다. 단계 표지가 찍힌 것과
 *    그 단계가 성공한 것은 다른 사실이다(`sampleFlowModel.ts` 머리말).
 */
export function flowStateText(state: FlowCellState | 'unassigned', verdict?: StageVerdict): string {
  if (verdict === 'fail') return t('monitoring.flow.cell.fail')
  switch (state) {
    case 'active':
      return t('monitoring.flow.cell.active')
    case 'marked':
      return verdict === 'pass' ? t('monitoring.flow.cell.pass') : t('monitoring.flow.cell.marked')
    case 'halted':
      return t('monitoring.flow.cell.halted')
    case 'unassigned':
      return t('monitoring.flow.groups.unassigned')
    default:
      return t('monitoring.flow.cell.pending')
  }
}

/**
 * 상태 표시에 붙는 수식자 — **단계 줄의 `class` 와 챔버 그림의 상태가 같은 말을 쓴다.**
 * 🔴 반환이 `string` 이 아니라 **닫힌 집합**이다 — 챔버 그림(`ChamberFigureState`)이 이 값을
 *    그대로 받으므로, 새 상태가 생기면 그림 쪽에서 타입으로 걸린다.
 */
export function flowStateKey(
  state: FlowCellState | 'unassigned',
  verdict?: StageVerdict,
): ChamberFigureState {
  return verdict === 'fail' ? 'fail' : state
}

/**
 * 카드·칩이 읽히는 말 — **「단계 + 상태」**(「염색 진행 중」). 단계를 모르면 상태만 말한다.
 * ⚠ 단계 이름은 **계약 어휘**(`enum.sampleStage.*`)를 그대로 쓴다 — 새로 짓지 않는다.
 */
export function chamberText(dot: BundleState, stageLabel: string | undefined): string {
  const state = flowStateText(dot.state, dot.verdict)
  return stageLabel === undefined || dot.state === 'unassigned' ? state : `${stageLabel} ${state}`
}

/**
 * **챔버가 말하는 것은 «담김 / 빈 / 실패» 셋뿐이다**(eric 지시 2026-09-22 — 「같은 말을 세 번
 * 하지 마라」).
 *
 * 종전 화면은 한 카드가 같은 사실을 **세 번** 말했다 — 챔버 안의 `●` · 단계 줄의 `●` ·
 * 상태 줄의 「등록 지남」. 셋이 같은 값(`flowStateKey`)에서 나왔기 때문이다.
 *
 * ⇒ 자리마다 말하는 축을 가른다.
 * | 자리 | 무엇을 말하나 |
 * | --- | --- |
 * | 챔버 그림 | **이 챔버에 시료가 들었나** — 담김 · 빈 · 실패 · 끝남 |
 * | 단계 줄 | **어디까지 왔나** (5칸 + 시각) |
 * | 아래 한 줄 | **지금 무엇을 하고 있나 + 경과** |
 *
 * 🔴 **「대기」와 「진행 중」이 둘 다 «담김» 이다.** 시료가 그 챔버에 배정돼 있으면 챔버는
 *    차 있는 것이고, 어느 단계인지는 **단계 줄이 말한다.** 여기서 단계를 되풀이하지 마라.
 * 🔴 **그림 컴포넌트의 상태 6종은 줄이지 않았다** — 그림은 공용이고 다른 자리(장비 관점)가
 *    여섯을 다 쓸 수 있다. 줄이는 것은 **이 카드가 무엇을 넘기느냐**다.
 */
export function chamberFill(dot: BundleState): ChamberFigureState {
  if (dot.verdict === 'fail') return 'fail'
  switch (dot.state) {
    /* 누구 시료인지 모른다 — 빈 칸 + 점선(「아직 없음」) */
    case 'unassigned':
      return 'unassigned'
    /* 폐기·중단 — 이 챔버의 일은 끝났다 */
    case 'halted':
      return 'halted'
    /* 🔴 나머지는 전부 **담김**이다. 단계는 단계 줄이 말한다 */
    default:
      return 'marked'
  }
}

/** 챔버 하나가 스크린리더에 읽히는 말. 🔴 **단계를 말하지 않는다**(단계 줄이 말한다) */
export function chamberFillText(dot: BundleState): string {
  switch (chamberFill(dot)) {
    case 'fail':
      return t('monitoring.flow.cell.fail')
    case 'unassigned':
      return t('monitoring.flow.groups.unassigned')
    case 'halted':
      return t('monitoring.flow.cell.halted')
    default:
      return t('monitoring.chamber.loaded')
  }
}

/**
 * **그 시료가 배치와 다를 때만 서는 한 줄** — eric 2026-09-23 「배치와 다를 때만 한 줄」.
 *
 * 🔴 **같으면 «아무 글자도 없다».** 단계 줄은 구역 머리에 배치 단위로 한 벌뿐이고, 카드가
 *    같은 말을 되풀이하면 텍스트를 뺀 뜻이 없어진다.
 * 🔴 **갈리는 축은 셋뿐이고 전부 서버 값이다** —
 *    ⓐ `verdict === 'fail'`(주입된 판정 · 지금 서버는 전건 `null` 이라 실물에서는 안 뜬다 · BR-49)
 *    ⓑ `status === 'discarded'`(시료마다 갈린다 — 배치 전체 쓰기가 폐기 시료를 건너뛴다)
 *    ⓒ **뒤처짐** — 그 시료의 표지가 배치의 표지보다 앞 자리에 멈춰 있다
 * ⚠ 시료 없는 묶음(「미배정」·「배정 모름」)은 여기서 말하지 않는다 — 챔버 아래 한 마디가 이미 그 말을 한다.
 */
export interface BundleDeviation {
  /** `flowVocabulary` 글리프 표와 같은 기호 */
  glyph: string
  /** 한 줄 문구. 🔴 **어휘를 새로 만들지 않는다** — 단계 이름 + 상태 이름의 조합이다 */
  text: string
  /**
   * 🔴 **낱말 하나**(「폐기」·「실패」·「대기」) — `text` 에서 단계 이름을 뺀 것.
   *    챔버 아래 줄은 모듈 이름을 **칩**이 말하므로 낱말만 필요하다(`chamberStageNotes`).
   * ⚠ 두 값을 각자 지어내지 않는다 — 한 자리에서 같이 만들어야 둘이 안 갈린다.
   */
  word: string
  /** 색·굵기를 고르는 축. 🔴 색«만»으로 가르지 않으므로 글리프·글자가 같이 말한다 */
  tone: 'fail' | 'halted' | 'behind'
}

export function bundleDeviation(input: {
  state: BundleState
  /** 시료 처분. 배정을 모르면 `undefined` */
  status?: SampleStatus | undefined
  /** 그 시료가 받은 마지막 표지의 자리(`stageIndex`) */
  sampleStage: number
  /** 배치가 받은 마지막 표지의 자리 */
  batchStage: number
}): BundleDeviation | undefined {
  const { state, status, sampleStage, batchStage } = input
  /* 배정을 모르면 「다르다」고 말할 근거가 없다 — 비교할 값 자체가 없다 */
  if (state.state === 'unassigned') return undefined

  const stageLabel =
    state.stage === undefined ? undefined : enumLabel('enum.sampleStage', state.stage)
  const join = (word: string): string => (stageLabel === undefined ? word : `${stageLabel} ${word}`)

  if (state.verdict === 'fail') {
    return {
      glyph: flowGlyph('marked', 'fail'),
      text: join(t('monitoring.flow.cell.fail')),
      word: t('monitoring.flow.cell.fail'),
      tone: 'fail',
    }
  }
  if (status === 'discarded') {
    return {
      glyph: flowGlyph('halted'),
      text: join(enumLabel('enum.sampleStatus', 'discarded')),
      word: enumLabel('enum.sampleStatus', 'discarded'),
      tone: 'halted',
    }
  }
  /* 🔴 뒤처짐 — 배치는 표지를 받았는데 이 시료는 아직이다. 「모른다」가 아니라 «다르다»다 */
  if (sampleStage >= 0 && sampleStage < batchStage) {
    return {
      glyph: flowGlyph('pending'),
      text: join(t('monitoring.flow.cell.pending')),
      word: t('monitoring.flow.cell.pending'),
      tone: 'behind',
    }
  }
  return undefined
}

/**
 * **그 챔버가 서 있는 공정 자리** — eric 2026-09-23 「원심분리기가 끝나면 하이픽 챔버한테
 * 예상되는 변화 모양이 있잖아 … 각각의 상태를 표현 가능하게 해줘」.
 *
 * 🔴 **출처는 «배치 표지» 하나다**(`batchFlowCells`). 화면이 단계를 추론하지 않는다 —
 *    진행 중인 칸이 있으면 그 단계를 «하는 중»(`done: false`), 없으면 마지막 표지가 «끝난»
 *    단계다(`done: true`).
 * 🔴 **배치와 다른 시료는 «그 시료가 멈춘 자리»에 선다**(폐기·실패). 배치가 염색까지 갔어도
 *    원심에서 폐기된 시료의 챔버는 원심 모습 그대로다 — 그게 사실이기 때문이다.
 * 🔴 **표지가 하나도 없으면 `undefined`** — 우물 안을 지어내지 않는다(상태 6종 형태만).
 */
export function chamberPhase(
  batchCells: readonly FlowCell[],
  frozenAt?: number,
): ChamberPhase | undefined {
  if (frozenAt !== undefined) {
    const cell = batchCells[frozenAt]
    return cell === undefined ? undefined : { stage: cell.stage, done: true }
  }
  const active = batchCells.find((cell) => cell.state === 'active')
  if (active !== undefined) return { stage: active.stage, done: false }
  const marked = [...batchCells].reverse().find((cell) => cell.marked)
  return marked === undefined ? undefined : { stage: marked.stage, done: true }
}

/**
 * **챔버 아래 한 마디 — 「지금 어디까지」 모듈 하나와 상태 하나.**
 *
 * eric 지시(2026-09-23, 세 번 깎였다):
 * ① 「각각의 챔버 아래에 **모든 이벤트가 나오지 말고, 어디까지 끝냈는지만** 간단하게 표현해줘.」
 * ② 「원심분리기(이거 **칩 형태**) **준비 중, 진행, 완료** 이렇게 해줘.」
 * ③ 「원심분리기, 어셈블러, 염색기 이렇게 **명칭을 다 써줘**.」
 * ④ 🔴 **「아니 다 적지 말고, «최종만» 적으라고. 여기서는 염색기 진행이 되어야지.」**
 *
 * ⇒ ③ 은 「세 모듈을 나열하라」가 **아니라** 「`원심` 말고 **`원심분리기`** 전체 이름을 쓰라」는
 *   뜻이었다(④ 가 그것을 못박았다). **칩은 «하나»다.**
 *
 * ```
 * [염색기] 진행
 * ```
 *
 * 🔴 **낱말은 셋뿐이다** — 준비 중 · 진행 · 완료. 「진행 «중»」이 아니다. 시각도, 이벤트
 *    이름(`move_to 시작` 류)도, 스텝 수도 적지 않는다 — 그것이 ① 이 걷으라 한 「모든 이벤트」다.
 * 🔴 **화면이 단계를 추론하지 않는다** — 자리는 `chamberPhase()` 가 «배치 표지»에서 만든 것을
 *    그대로 쓴다(폐기·실패 시료는 그 시료가 멈춘 자리로 언 채로 온다).
 * 🔴 **폐기·실패는 «멈춘 모듈»에 그 낱말이 선다**(「실패」·「폐기」) — 낱말 셋의 예외다.
 */
export interface ChamberStageNote {
  /** 🔴 모듈 **전체 이름**으로 적는다(「원심」 아니라 「원심분리기」) — 라벨은 `moduleLabel` 이 붙인다 */
  device: MonitoringDeviceKey
  word: string
  /**
   * 🔴 **칩 글자를 모듈 이름 대신 «단계 이름»으로** — 웰 근거(분주 기록)일 때만(「분주 완료」).
   *    모듈이 아직 도는 중인데 챔버 하나가 「염색기 완료」라고 말하지 않게 한다(eric 2026-09-28).
   */
  label?: string
  /** 칩 + 낱말의 툴팁(분주 바퀴의 스텝 등) — 값이 있을 때만 */
  title?: string
  /** 편차일 때만. 색«만»으로 가르지 않으므로 낱말이 이미 같은 말을 한다 */
  tone?: BundleDeviation['tone']
  /**
   * 낱말 셋(준비 중 · 진행 · 완료) 중 어느 것인가 — **상태 배지의 종류**를 고르는 축(디자인팀 시안 2026-10-01 §3
   * 「공정명 + 상태 배지」). 🔴 낱말과 **같은 자리에서** 같이 정한다 — 글자를 다시 읽어 종류를 짐작하지 않는다.
   * 편차(`tone`)면 없다 — 그때는 편차가 종류를 정한다(`BundleChamber` 의 `noteBadgeStatus`).
   * 🔴 `stage` — **그 단계에 와 있음(챔버별 진행 모름)**. 배지가 안 서고 공정명만 선다(`chamberNote` · eric 2026-10-06).
   *    「진행」 배지를 아홉 칸에 같이 세우지 않으려는 자리다 — 서버가 웰을 말하면 `doing` 으로 돌아간다.
   * `transfer` — **6축 이송 중**(공정명 자리에 「6축 로봇」 · 배지 「이송 중」 · 중립 꼴 · 팀장 (가)안 2026-10-06).
   */
  status?: 'ready' | 'doing' | 'done' | 'stage' | 'transfer'
}

/**
 * 🔴 **멈춘 자리**(폐기·실패). `at` 은 `FLOW_STAGES` 색인이고 `word` 는 **낱말 하나**다
 *    (「원심 폐기」가 아니라 「폐기」 — 모듈 이름은 칩이 이미 말한다).
 */
export interface ChamberHalt {
  at: number
  word: string
  tone: BundleDeviation['tone']
}

/**
 * 단계 → 그 단계를 말할 때 세울 모듈 칩.
 *
 * 🔴 **`STAGE_DEVICE`(`sampleFlowModel`)의 «확장»이다** — 그쪽은 장비가 하는 단계 셋만 알고,
 *    여기는 단계 다섯을 전부 덮어야 한다. 갈리는 두 칸에 뜻이 있다:
 *    ⓐ `registered` → **원심분리기**(아직 아무 모듈도 안 돈다 → 「준비 중」으로 적는다)
 *    ⓑ `completed` → **염색기**(마지막으로 일한 모듈이 그것이다)
 * ⚠ 표를 둘로 두지 않으려고 `STAGE_DEVICE` 를 늘리는 길도 있었지만, 그쪽은 「그 단계를 **하는**
 *   장비」라는 뜻이라 `registered`·`completed` 를 넣으면 **그 뜻이 깨진다**(그 표를 읽는
 *   `deviceOfStage`·`DEVICE_STAGES` 가 같이 흔들린다).
 */
export const NOTE_DEVICE: Record<SampleStage, MonitoringDeviceKey> = {
  registered: 'cent',
  centrifuged: 'cent',
  assembled: 'asm',
  staining: 'stain',
  completed: 'stain',
}

export function chamberStageNote(
  batchCells: readonly FlowCell[],
  halt?: ChamberHalt,
): ChamberStageNote | undefined {
  const phase = chamberPhase(batchCells, halt?.at)
  /* 🔴 표지가 하나도 없으면 «아무 말도 안 한다** — 자리를 지어내지 않는다 */
  if (phase === undefined) return undefined
  const device = NOTE_DEVICE[phase.stage]

  /* 🔴 멈춘 시료는 그 자리의 모듈에 편차 낱말이 선다(낱말 셋의 예외) */
  if (halt !== undefined) return { device, word: halt.word, tone: halt.tone }

  /*
   * 🔴 **`registered` 는 「완료」가 아니라 「준비 중」이다.** 등록 표지가 찍힌 것이지 원심이
   *    끝난 것이 아니다 — `chamberPhase` 는 「마지막 표지」만 알아서 `done: true` 로 준다.
   *    이 한 줄이 없으면 갓 등록한 배치의 챔버가 **「원심분리기 완료」**라고 거짓을 말한다.
   */
  if (phase.stage === 'registered') {
    return { device, word: t('monitoring.chamber.ready'), status: 'ready' }
  }

  return {
    device,
    word: t(phase.done ? 'monitoring.chamber.done' : 'monitoring.chamber.doing'),
    status: phase.done ? 'done' : 'doing',
  }
}
